import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarClock, Loader2, Save, Plus, Printer, Trash2, Settings2, Wand2, FileDown, Pencil } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const ap = h < 12 ? "AM" : "PM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m || 0).padStart(2, "0")} ${ap}`;
};
const timeRange = (x) => (x && x.start ? `${fmtTime(x.start)} – ${fmtTime(x.end)}` : "");

const AF_DEFAULT = { enabled: false, start: "08:00", assembly: 15, period: 40, break_min: 15 };
const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
const toStr = (mins) => { mins = ((mins % 1440) + 1440) % 1440; return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`; };

// ordered columns/rows: periods interleaved with breaks (full-width across all days)
const buildSlots = (periods, breaks) => {
  const out = [];
  breaks.forEach((b, bi) => { if (Number(b.after) === -1) out.push({ type: "break", bi, b }); });
  periods.forEach((p, pi) => {
    out.push({ type: "period", pi, p });
    breaks.forEach((b, bi) => { if (Number(b.after) === pi) out.push({ type: "break", bi, b }); });
  });
  return out;
};

const clsLabel = (c) => `${c.name}${c.section ? `-${c.section}` : ""}`;

// Day-schedule print (rows = days). rows: [{label, get(pi)->{primary,secondary}}]
function printSchedule(title, slots, rows) {
  const headCells = slots.map((s) => {
    if (s.type === "break") return `<th class="brk">${s.b.label || "Break"}${timeRange(s.b) ? `<div class="sub">${timeRange(s.b)}</div>` : ""}</th>`;
    return `<th>${s.p.name}${timeRange(s.p) ? `<div class="sub">${timeRange(s.p)}</div>` : ""}</th>`;
  }).join("");
  const bodyRows = rows.map((r) => {
    const cells = slots.map((s) => {
      if (s.type === "break") return `<td class="brk">${s.b.label || "Break"}</td>`;
      const c = r.get(s.pi);
      return `<td>${c.primary ? `<div class="subj">${c.primary}</div>` : "—"}${c.secondary ? `<div class="tch">${c.secondary}</div>` : ""}</td>`;
    }).join("");
    return `<tr><td class="day">${r.label}</td>${cells}</tr>`;
  }).join("");
  openPrint(title, `<table><thead><tr><th class="day">Day</th>${headCells}</tr></thead><tbody>${bodyRows}</tbody></table>`);
}

function openPrint(title, tableHtml) {
  const html = `<!doctype html><html><head><title>${title}</title><style>
    *{box-sizing:border-box;font-family:Arial,Helvetica,sans-serif}
    body{margin:24px;color:#1e293b}
    h1{font-size:20px;margin:0 0 2px;text-align:center}
    .meta{color:#64748b;font-size:12px;margin:0 0 14px;text-align:center}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th,td{border:1px solid #cbd5e1;padding:6px 7px;text-align:center;vertical-align:middle}
    th{background:#f1f5f9}
    th .sub{font-weight:400;color:#64748b;font-size:9px;margin-top:2px}
    td.day{background:#f8fafc;font-weight:700;text-align:left;white-space:nowrap}
    td .subj{font-weight:600}
    td .tch{color:#64748b;font-size:9px}
    th.brk,td.brk{background:#fef3c7;color:#92400e;font-weight:600}
    @media print{body{margin:10mm}}
  </style></head><body><h1>${title}</h1><div class="meta">Generated ${new Date().toLocaleDateString()}</div>${tableHtml}
  <script>window.onload=function(){window.print();}</script></body></html>`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Enable pop-ups to print"); return; }
  w.document.write(html); w.document.close();
}

// Download the school's period + break bell schedule as a Word (.doc) file.
function downloadSchedule(schoolName, periods, breaks) {
  const slots = buildSlots(periods || [], breaks || []);
  if (!slots.length) { toast.error("No periods configured yet"); return; }
  const rows = slots.map((s, i) => {
    const o = s.type === "break"
      ? { name: s.b.label || "Break", type: "Break", start: s.b.start, end: s.b.end, bg: "#fef3c7" }
      : { name: s.p.name, type: "Period", start: s.p.start, end: s.p.end, bg: "#ffffff" };
    return `<tr style="background:${o.bg}"><td style="text-align:center">${i + 1}</td><td>${o.name}</td><td style="text-align:center">${o.type}</td><td style="text-align:center">${o.start ? fmtTime(o.start) : "—"}</td><td style="text-align:center">${o.end ? fmtTime(o.end) : "—"}</td></tr>`;
  }).join("");
  const title = `${schoolName ? schoolName + " — " : ""}Daily Period & Break Schedule`;
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${title}</title></head><body style="font-family:Arial,Helvetica,sans-serif;color:#1e293b">
    <h2 style="margin:0 0 4px">${title}</h2>
    <p style="color:#64748b;font-size:12px;margin:0 0 14px">Generated ${new Date().toLocaleDateString()}</p>
    <table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;width:100%;font-size:13px">
      <thead><tr style="background:#f1f5f9"><th style="width:36px">#</th><th style="text-align:left">Name</th><th style="width:70px">Type</th><th style="width:90px">Start</th><th style="width:90px">End</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </body></html>`;
  const blob = new Blob(["\ufeff", html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Timetable_Schedule${schoolName ? "_" + schoolName.replace(/[^a-z0-9]+/gi, "_") : ""}.doc`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Day-schedule grid (rows = days, cols = periods) for one class. getCell(day, pi) -> {subject, teacher}
function ScheduleGrid({ slots, getCell }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm border-collapse" data-testid="timetable-grid">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr>
            <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 z-10">Day</th>
            {slots.map((s, i) => s.type === "break" ? (
              <th key={i} className="px-2 py-2 text-center min-w-[120px] bg-amber-50 text-amber-700 align-top">
                <div className="font-semibold normal-case">{s.b.label || "Break"}</div>
                {timeRange(s.b) && <div className="text-[10px] font-normal normal-case mt-0.5">{timeRange(s.b)}</div>}
              </th>
            ) : (
              <th key={i} className="px-2 py-2 text-left min-w-[150px] align-top">
                <div className="normal-case font-semibold text-slate-600">{s.p.name}</div>
                {timeRange(s.p) && <div className="text-[10px] font-normal normal-case text-slate-400 mt-0.5">{timeRange(s.p)}</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {DAYS.map((day) => (
            <tr key={day} data-testid={`timetable-row-${day}`}>
              <td className="px-3 py-2 font-medium text-slate-700 sticky left-0 bg-white z-10">{day}</td>
              {slots.map((s, i) => s.type === "break" ? (
                <td key={i} className="px-2 py-2 text-center align-middle bg-amber-50 text-amber-700 text-xs font-medium">{s.b.label || "Break"}</td>
              ) : (
                <td key={i} className="px-2 py-2 align-top">
                  <div className="text-xs">
                    <div className="font-medium text-slate-700">{getCell(day, s.pi).subject || "—"}</div>
                    {getCell(day, s.pi).teacher && <div className="text-slate-400">{getCell(day, s.pi).teacher}</div>}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Timetable() {
  const { user } = useAuth();
  const canEdit = hasPerm(user, "manage_settings");
  const [tab, setTab] = useState("editor");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [settings, setSettings] = useState({ periods: [], breaks: [] });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [schoolName, setSchoolName] = useState("");

  // ONE shared client-side data source for all three views: { classId: { day: [ {subject,teacher}, ... ] } }
  const [grids, setGrids] = useState({});
  const [gridsLoading, setGridsLoading] = useState(true);

  // master editor
  const [masterDay, setMasterDay] = useState("Monday");
  const [dirty, setDirty] = useState(() => new Set());
  const [savingMaster, setSavingMaster] = useState(false);

  // by class / by teacher selections
  const [viewClassId, setViewClassId] = useState("");
  const [teacherName, setTeacherName] = useState("");

  // settings draft
  const [draft, setDraft] = useState({ periods: [], breaks: [] });
  const [savingSettings, setSavingSettings] = useState(false);

  const loadGrids = () => {
    setGridsLoading(true);
    api.get("/timetables").then((r) => {
      const m = {};
      (r.data || []).forEach((tt) => { m[tt.class_id] = tt.grid || {}; });
      setGrids(m); setDirty(new Set());
    }).catch(() => setGrids({})).finally(() => setGridsLoading(false));
  };

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/subjects").then((r) => setSubjects(r.data.filter((s) => s.for_timetable))).catch(() => {});
    api.get("/staff").then((r) => setStaff(r.data.filter((s) => s.role !== "admin"))).catch(() => {});
    api.get("/settings").then((r) => setSchoolName(r.data?.school_name || "")).catch(() => {});
    api.get("/timetable-settings")
      .then((r) => { setSettings(r.data); setDraft(JSON.parse(JSON.stringify({ ...r.data, auto_fill: { ...AF_DEFAULT, ...(r.data.auto_fill || {}) } }))); })
      .finally(() => setLoadingSettings(false));
    loadGrids();
  }, []);

  const slots = useMemo(() => buildSlots(settings.periods || [], settings.breaks || []), [settings]);
  const noPeriods = !loadingSettings && (!settings.periods || settings.periods.length === 0);

  // ---- shared grid helpers ----
  const cellAt = (classId, day, pi) => grids[classId]?.[day]?.[pi] || { subject: "", teacher: "" };
  const setCellAt = (classId, day, pi, key, val) => {
    setGrids((g) => {
      const cg = { ...(g[classId] || {}) };
      const row = [...(cg[day] || [])];
      while (row.length <= pi) row.push({ subject: "", teacher: "" });
      row[pi] = { ...row[pi], [key]: val };
      cg[day] = row;
      return { ...g, [classId]: cg };
    });
    setDirty((d) => new Set(d).add(classId));
  };

  const saveMaster = async () => {
    const ids = [...dirty];
    if (!ids.length) { toast.message("No changes to save"); return; }
    setSavingMaster(true);
    try {
      await Promise.all(ids.map((cid) => api.put("/timetable", {
        class_id: cid, days: DAYS,
        periods: (settings.periods || []).map((p) => p.name),
        period_times: (settings.periods || []).map((p) => ({ start: p.start || "", end: p.end || "" })),
        breaks: settings.breaks || [], grid: grids[cid] || {},
      })));
      toast.success("Timetable saved");
      setDirty(new Set());
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingMaster(false); }
  };

  // ---- by teacher aggregation (reads shared grids) ----
  const teacherCell = (day, pi) => {
    if (!teacherName) return { subject: "", teacher: "" };
    const hits = [];
    for (const c of classes) {
      const cell = grids[c.id]?.[day]?.[pi];
      if (cell && cell.teacher === teacherName) hits.push({ cls: clsLabel(c), subject: cell.subject || "" });
    }
    if (!hits.length) return { subject: "", teacher: "" };
    return { subject: hits.map((h) => h.cls).join(", "), teacher: hits.map((h) => h.subject).filter(Boolean).join(", ") };
  };

  // ---- prints ----
  const createdOn = `Timetable Created on ${new Date().toLocaleDateString()}`;
  const printMaster = () => {
    const headCells = classes.map((c) => `<th>${clsLabel(c)}</th>`).join("");
    const bodyRows = slots.map((s) => {
      if (s.type === "break") return `<tr><td class="day">${s.b.label || "Break"}${timeRange(s.b) ? `<div class="sub">${timeRange(s.b)}</div>` : ""}</td><td class="brk" colspan="${classes.length}">${s.b.label || "Break"}</td></tr>`;
      const cells = classes.map((c) => { const cell = cellAt(c.id, masterDay, s.pi); return `<td>${cell.subject ? `<div class="subj">${cell.subject}</div>` : "—"}${cell.teacher ? `<div class="tch">${cell.teacher}</div>` : ""}</td>`; }).join("");
      return `<tr><td class="day">${s.p.name}${timeRange(s.p) ? `<div class="sub">${timeRange(s.p)}</div>` : ""}</td>${cells}</tr>`;
    }).join("");
    openPrint(`${schoolName ? schoolName + " — " : ""}${createdOn} (${masterDay})`, `<table><thead><tr><th class="day">Classes / Periods</th>${headCells}</tr></thead><tbody>${bodyRows}</tbody></table>`);
  };
  const printByClass = () => {
    const cls = classes.find((c) => c.id === viewClassId);
    const rows = DAYS.map((day) => ({ label: day, get: (pi) => { const c = cellAt(viewClassId, day, pi); return { primary: c.subject, secondary: c.teacher }; } }));
    printSchedule(`${cls ? clsLabel(cls) : "Class"} — Weekly Timetable`, slots, rows);
  };
  const printByTeacher = () => {
    const rows = DAYS.map((day) => ({ label: day, get: (pi) => { const c = teacherCell(day, pi); return { primary: c.subject, secondary: c.teacher }; } }));
    printSchedule(`${teacherName} — Weekly Schedule`, slots, rows);
  };
  const topPrint = () => {
    if (tab === "editor") printMaster();
    else if (tab === "byclass") { if (!viewClassId) { toast.error("Select a class to print"); return; } printByClass(); }
    else if (tab === "teacher") { if (!teacherName) { toast.error("Select a teacher to print"); return; } printByTeacher(); }
    else toast.error("Open Timetable, by Class or by Teacher to print");
  };

  // ---- settings ----
  const addPeriod = () => setDraft((d) => ({ ...d, periods: [...d.periods, { name: `Period ${d.periods.length + 1}`, start: "", end: "" }] }));
  const setPeriod = (i, key, val) => setDraft((d) => ({ ...d, periods: d.periods.map((p, idx) => idx === i ? { ...p, [key]: val } : p) }));
  const removePeriod = (i) => setDraft((d) => ({ ...d, periods: d.periods.filter((_, idx) => idx !== i), breaks: d.breaks.filter((b) => Number(b.after) < d.periods.length - 1 || Number(b.after) === -1) }));
  const addBreak = () => setDraft((d) => ({ ...d, breaks: [...d.breaks, { after: 0, label: "Break", start: "", end: "", duration: Number(d.auto_fill?.break_min) || 15 }] }));
  const setBreak = (i, key, val) => setDraft((d) => ({ ...d, breaks: d.breaks.map((b, idx) => idx === i ? { ...b, [key]: key === "after" ? Number(val) : val } : b) }));
  const removeBreak = (i) => setDraft((d) => ({ ...d, breaks: d.breaks.filter((_, idx) => idx !== i) }));

  const setAf = (key, val) => setDraft((d) => ({ ...d, auto_fill: { ...(d.auto_fill || AF_DEFAULT), [key]: val } }));
  const computeAutoFill = (d) => {
    const af = d.auto_fill || {};
    if (!af.start) { toast.error("Set a start time for Auto Fill"); return null; }
    const per = Number(af.period) || 0, brk = Number(af.break_min) || 0;
    if (per <= 0) { toast.error("Period timing must be greater than 0"); return null; }
    let clock = toMin(af.start);
    const periods = d.periods.map((p) => ({ ...p }));
    const breaks = d.breaks.map((b) => ({ ...b }));
    for (const s of buildSlots(periods, breaks)) {
      if (s.type === "period") { const st = clock; clock += per; periods[s.pi].start = toStr(st); periods[s.pi].end = toStr(clock); }
      else { const bd = Number(breaks[s.bi].duration) || brk || 15; const st = clock; clock += bd; breaks[s.bi].start = toStr(st); breaks[s.bi].end = toStr(clock); }
    }
    return { ...d, periods, breaks };
  };
  const applyAutoFill = () => { const r = computeAutoFill(draft); if (r) { setDraft(r); toast.success("Times filled in — review and Save Settings"); } };

  const saveSettings = async () => {
    if (!draft.periods.length) { toast.error("Add at least one period"); return; }
    if (draft.periods.some((p) => !p.name.trim())) { toast.error("Every period needs a name"); return; }
    let payload = draft;
    if (draft.auto_fill?.enabled) { payload = computeAutoFill(draft); if (!payload) return; setDraft(JSON.parse(JSON.stringify(payload))); }
    setSavingSettings(true);
    try {
      const r = await api.put("/timetable-settings", payload);
      setSettings(r.data); setDraft(JSON.parse(JSON.stringify({ ...r.data, auto_fill: { ...AF_DEFAULT, ...(r.data.auto_fill || {}) } })));
      toast.success("Timetable settings saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  const ClassPicker = ({ value, onChange, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-56" data-testid={testid}><SelectValue placeholder="Select class" /></SelectTrigger>
      <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{clsLabel(c)}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><CalendarClock className="text-indigo-500" size={26} />Timetable</h1>
          <p className="text-slate-500">Build class schedules, view them by class or teacher, and configure periods & breaks.</p>
        </div>
        {!noPeriods && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => downloadSchedule(schoolName, settings.periods, settings.breaks)} className="gap-1.5" data-testid="download-schedule-btn"><FileDown size={15} />Download</Button>
            <Button size="sm" variant="outline" onClick={topPrint} className="gap-1.5" data-testid="print-timetable-btn"><Printer size={15} />Print</Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="timetable-tabs">
          <TabsTrigger value="editor" data-testid="tab-timetable">Timetable</TabsTrigger>
          <TabsTrigger value="byclass" data-testid="tab-by-class">Timetable by Class</TabsTrigger>
          <TabsTrigger value="teacher" data-testid="tab-by-teacher">Timetable by Teacher</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-tt-settings">Timetable Settings</TabsTrigger>
        </TabsList>

        {/* Master editor: rows = Periods, cols = Classes (for the selected day) */}
        <TabsContent value="editor" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="font-heading text-lg font-bold text-slate-800">{createdOn}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={masterDay} onValueChange={setMasterDay}>
                <SelectTrigger className="w-40" data-testid="master-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              {canEdit && <Button onClick={saveMaster} disabled={savingMaster} className="gap-1.5" data-testid="save-timetable-btn">{savingMaster ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save</Button>}
            </div>
          </div>
          {noPeriods ? <div className="bg-white rounded-xl border border-slate-200 py-12 text-center text-slate-400">No periods configured. Go to <b>Timetable Settings</b> to add periods first.</div> :
            classes.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-12 text-center text-slate-400">No classes yet. Add classes first.</div> :
            gridsLoading ? <div className="bg-white rounded-xl border border-slate-200 py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="master-timetable-grid">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 z-10 min-w-[120px]"><div>Classes</div><div className="text-slate-400">Periods</div></th>
                      {classes.map((c) => <th key={c.id} className="px-2 py-2 text-center min-w-[150px] normal-case font-semibold text-slate-600">{clsLabel(c)}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {slots.map((s, si) => s.type === "break" ? (
                      <tr key={si} data-testid={`master-break-row-${s.bi}`}>
                        <td className="px-3 py-2 font-medium sticky left-0 bg-amber-50 text-amber-700 z-10">{s.b.label || "Break"}{timeRange(s.b) && <div className="text-[10px] font-normal">{timeRange(s.b)}</div>}</td>
                        <td colSpan={classes.length} className="px-2 py-2 text-center bg-amber-50 text-amber-700 text-xs font-medium">{s.b.label || "Break"}</td>
                      </tr>
                    ) : (
                      <tr key={si} data-testid={`master-period-row-${s.pi}`}>
                        <td className="px-3 py-2 font-medium text-slate-700 sticky left-0 bg-white z-10">{s.p.name}{timeRange(s.p) && <div className="text-[10px] font-normal text-slate-400">{timeRange(s.p)}</div>}</td>
                        {classes.map((c) => (
                          <td key={c.id} className="px-2 py-2 align-top">
                            {canEdit ? (
                              <div className="space-y-1">
                                <Select value={cellAt(c.id, masterDay, s.pi).subject || "none"} onValueChange={(v) => setCellAt(c.id, masterDay, s.pi, "subject", v === "none" ? "" : v)}>
                                  <SelectTrigger className="h-8 text-xs" data-testid={`master-subject-${c.id}-${s.pi}`}><SelectValue placeholder="Subject" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">—</SelectItem>
                                    {subjects.map((sub) => <SelectItem key={sub.id} value={sub.name}>{sub.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Select value={cellAt(c.id, masterDay, s.pi).teacher || "none"} onValueChange={(v) => setCellAt(c.id, masterDay, s.pi, "teacher", v === "none" ? "" : v)}>
                                  <SelectTrigger className="h-8 text-xs" data-testid={`master-teacher-${c.id}-${s.pi}`}><SelectValue placeholder="Teacher" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">—</SelectItem>
                                    {staff.map((st) => <SelectItem key={st.id} value={st.name}>{st.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : (
                              <div className="text-xs"><div className="font-medium text-slate-700">{cellAt(c.id, masterDay, s.pi).subject || "—"}</div>{cellAt(c.id, masterDay, s.pi).teacher && <div className="text-slate-400">{cellAt(c.id, masterDay, s.pi).teacher}</div>}</div>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          {canEdit && !noPeriods && classes.length > 0 && <p className="text-xs text-slate-400">Editing <b>{masterDay}</b>. Changes here also appear in the "by Class" and "by Teacher" tabs.</p>}
        </TabsContent>

        {/* By class (read-only; Edit jumps to master) */}
        <TabsContent value="byclass" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <ClassPicker value={viewClassId} onChange={setViewClassId} testid="byclass-select" />
            {canEdit && viewClassId && !noPeriods && <Button size="sm" variant="outline" onClick={() => setTab("editor")} className="gap-1" data-testid="byclass-edit-btn"><Pencil size={14} />Edit</Button>}
          </div>
          {noPeriods ? <div className="bg-white rounded-xl border border-slate-200 py-12 text-center text-slate-400">Configure periods in <b>Timetable Settings</b> first.</div> :
            !viewClassId ? <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400">Select a class to view its weekly timetable.</div> :
            gridsLoading ? <div className="bg-white rounded-xl border border-slate-200 py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
              <ScheduleGrid slots={slots} getCell={(day, pi) => cellAt(viewClassId, day, pi)} />}
        </TabsContent>

        {/* By teacher (read-only; Edit jumps to master) */}
        <TabsContent value="teacher" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Select value={teacherName} onValueChange={setTeacherName}>
              <SelectTrigger className="w-56" data-testid="byteacher-select"><SelectValue placeholder="Select teacher" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            {canEdit && !noPeriods && <Button size="sm" variant="outline" onClick={() => { setTab("editor"); toast.message("Pick a class column in the Timetable tab to edit"); }} className="gap-1" data-testid="byteacher-edit-btn"><Pencil size={14} />Edit</Button>}
          </div>
          {noPeriods ? <div className="bg-white rounded-xl border border-slate-200 py-12 text-center text-slate-400">Configure periods in <b>Timetable Settings</b> first.</div> :
            gridsLoading ? <div className="bg-white rounded-xl border border-slate-200 py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
            !teacherName ? <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400">Select a teacher to view their weekly schedule across all classes.</div> :
              <ScheduleGrid slots={slots} getCell={teacherCell} />}
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings" className="space-y-4">
          {!canEdit ? <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400">You don't have permission to edit timetable settings.</div> : (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="auto-fill-card">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={!!draft.auto_fill?.enabled} onCheckedChange={(v) => setAf("enabled", !!v)} data-testid="auto-fill-toggle" />
                  <span className="font-semibold text-slate-800 flex items-center gap-2"><Wand2 size={18} className="text-violet-500" />Auto Fill Time</span>
                </label>
                {draft.auto_fill?.enabled && (
                  <div className="mt-4 space-y-4">
                    <div className="flex flex-wrap items-end gap-3" data-testid="af-options-row">
                      <div className="w-36">
                        <Label className="text-xs text-slate-500">Start Time</Label>
                        <Input type="time" value={draft.auto_fill.start || ""} onChange={(e) => setAf("start", e.target.value)} className="mt-1" data-testid="af-start" />
                      </div>
                      <div className="w-36">
                        <Label className="text-xs text-slate-500">Period Timing (min)</Label>
                        <Input type="number" min="1" value={draft.auto_fill.period} onChange={(e) => setAf("period", e.target.value)} className="mt-1" data-testid="af-period" />
                      </div>
                      {draft.breaks.map((b, i) => (
                        <div key={i} className="w-36" data-testid={`af-break-row-${i}`}>
                          <Label className="text-xs font-medium text-amber-700 truncate block">{b.label || "Break"} (min)</Label>
                          <Input type="number" min="0" value={b.duration ?? draft.auto_fill.break_min} onChange={(e) => setBreak(i, "duration", Number(e.target.value))} className="mt-1" data-testid={`af-break-${i}`} />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-xs text-slate-500">Fills each period & break in sequence from the start time, alternating periods and breaks. Also applied automatically on Save.</p>
                      <Button size="sm" variant="outline" onClick={applyAutoFill} className="gap-1.5" data-testid="af-apply-btn"><Wand2 size={14} />Apply Now</Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Settings2 size={18} className="text-indigo-500" />Periods</h3>
                  <Button size="sm" variant="outline" onClick={addPeriod} className="gap-1" data-testid="add-period-setting-btn"><Plus size={14} />Add Period</Button>
                </div>
                <div className="space-y-2">
                  <div className="hidden sm:grid grid-cols-[1fr_140px_140px_40px] gap-2 text-xs font-medium text-slate-400 px-1">
                    <span>Period Name</span><span>Start Time</span><span>End Time</span><span></span>
                  </div>
                  {draft.periods.length === 0 && <p className="text-sm text-slate-400">No periods. Click "Add Period".</p>}
                  {buildSlots(draft.periods, draft.breaks).map((s, si) => s.type === "break" ? (
                    <div key={`b-${si}`} className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2" data-testid={`period-list-break-${s.bi}`}>
                      <CalendarClock size={14} className="text-amber-500 shrink-0" />
                      <span className="text-sm font-medium text-amber-700">{s.b.label || "Break"}</span>
                      {timeRange(s.b) && <span className="text-xs text-amber-600">{timeRange(s.b)}</span>}
                      <span className="text-[11px] text-amber-500/80 ml-auto">Edit in Breaks below</span>
                    </div>
                  ) : (
                    <div key={`p-${s.pi}`} className="grid grid-cols-2 sm:grid-cols-[1fr_140px_140px_40px] gap-2" data-testid={`period-setting-${s.pi}`}>
                      <Input value={s.p.name} onChange={(e) => setPeriod(s.pi, "name", e.target.value)} placeholder={`Period ${s.pi + 1}`} data-testid={`period-name-${s.pi}`} />
                      <Input type="time" value={s.p.start || ""} onChange={(e) => setPeriod(s.pi, "start", e.target.value)} data-testid={`period-start-${s.pi}`} />
                      <Input type="time" value={s.p.end || ""} onChange={(e) => setPeriod(s.pi, "end", e.target.value)} data-testid={`period-end-${s.pi}`} />
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-rose-500" onClick={() => removePeriod(s.pi)} data-testid={`remove-period-${s.pi}`}><Trash2 size={15} /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2"><CalendarClock size={18} className="text-amber-500" />Breaks</h3>
                  <Button size="sm" variant="outline" onClick={addBreak} className="gap-1" data-testid="add-break-setting-btn"><Plus size={14} />Add Break</Button>
                </div>
                <div className="space-y-3">
                  {draft.breaks.length === 0 && <p className="text-sm text-slate-400">No breaks. Breaks appear as full-width bands across all days.</p>}
                  {draft.breaks.map((b, i) => (
                    <div key={i} className="grid sm:grid-cols-[1fr_1fr_120px_120px_40px] gap-2 items-end border border-slate-100 rounded-lg p-3" data-testid={`break-setting-${i}`}>
                      <div>
                        <Label className="text-xs text-slate-500">Label</Label>
                        <Input value={b.label} onChange={(e) => setBreak(i, "label", e.target.value)} placeholder="Lunch" className="mt-1" data-testid={`break-label-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Position</Label>
                        <Select value={String(b.after)} onValueChange={(v) => setBreak(i, "after", v)}>
                          <SelectTrigger className="mt-1" data-testid={`break-after-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="-1">Before {draft.periods[0]?.name || "first"}</SelectItem>
                            {draft.periods.map((p, pi) => <SelectItem key={pi} value={String(pi)}>After {p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Start</Label>
                        <Input type="time" value={b.start || ""} onChange={(e) => setBreak(i, "start", e.target.value)} className="mt-1" data-testid={`break-start-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">End</Label>
                        <Input type="time" value={b.end || ""} onChange={(e) => setBreak(i, "end", e.target.value)} className="mt-1" data-testid={`break-end-${i}`} />
                      </div>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-rose-500" onClick={() => removeBreak(i)} data-testid={`remove-break-${i}`}><Trash2 size={15} /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button onClick={saveSettings} disabled={savingSettings} className="gap-1.5" data-testid="save-tt-settings-btn">{savingSettings ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save Settings</Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
